import { Add, ErrorOutline } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import type { Control } from "react-hook-form";

import { useCommissionDistributionTable } from "CommissionDistribution/Hooks/useCommissionDistributionTable";
import {
	type ConsultantOption,
	MAX_NON_REALIZATOR_PARTICIPANTS,
	ParticipantType,
} from "CommissionDistribution/Models/types";
import {
	CommissionDistributionResource,
	useCommissionDistributionResource,
} from "CommissionDistribution/Translations/CommissionDistributionResource";
import type { CommissionDistributionFormValues } from "CommissionDistribution/Utils/commissionDistributionSchema";

type Props = {
	control: Control<CommissionDistributionFormValues>;
	consultants: ConsultantOption[];
	realizator: ConsultantOption | null;
};

export const CommissionDistributionTable = ({
	control,
	consultants,
	realizator,
}: Props) => {
	const { t } = useCommissionDistributionResource();
	const R = CommissionDistributionResource;

	const { table, fields, append, isOverLimit } = useCommissionDistributionTable(
		{
			control,
			consultants,
			realizator,
		},
	);

	const handleAddRow = () => {
		if (fields.length >= MAX_NON_REALIZATOR_PARTICIPANTS) return;
		append({
			participantType: ParticipantType.ContractTipster,
			consultantId: "",
			amount: 0,
		});
	};

	return (
		<Box>
			<MaterialReactTable table={table} />

			<Button
				onClick={handleAddRow}
				disabled={fields.length >= MAX_NON_REALIZATOR_PARTICIPANTS}
				startIcon={<Add />}
				sx={{
					width: "100%",
					justifyContent: "flex-start",
					borderTop: "1px dashed",
					borderColor: "divider",
					borderRadius: 0,
					color: "text.secondary",
					py: 1.5,
					px: 2,
				}}
			>
				<Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
					{t(R.Dialog.AddParticipant)}
				</Typography>
				<Typography variant="caption" color="text.disabled">
					{fields.length} / {MAX_NON_REALIZATOR_PARTICIPANTS}
				</Typography>
			</Button>

			{isOverLimit && (
				<Alert
					severity="error"
					variant="filled"
					iconMapping={{ error: <ErrorOutline fontSize="inherit" /> }}
					sx={{
						mt: 2,
						alignItems: "center",
						fontSize: 14,
						fontWeight: 600,
						boxShadow: 3,
						"& .MuiAlert-icon": { fontSize: 28 },
					}}
				>
					<AlertTitle sx={{ fontWeight: 800, mb: 0 }}>
						{t(R.Dialog.ValidationOver100)}
					</AlertTitle>
				</Alert>
			)}
		</Box>
	);
};
